import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IRecipePermissionDTO } from 'data/dtos/automation/i-recipe-permission-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import RecipePermissionSchema, { IRecipePermissionFormData } from 'schema/automation/recipe-permission'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const RecipePermissionForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [recipesA, setrecipesA] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IRecipePermissionFormData>({
    resolver: ajvResolver(RecipePermissionSchema),
    defaultValues: {
      recipeId: '',
      execute: false,
      edit: false,
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/recipes-permissions/${id}`)
        .then((response) => {
          const { data } = response.data

          const recipePermissionResult = {
            recipeId: data.recipeId.id,
            execute: data.execute,
            edit: data.edit,
            isInactive: data.isInactive,
          }

          return recipePermissionResult
        })
        .then((recipePermissionResult: IRecipePermissionDTO) => {
          reset(recipePermissionResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IRecipePermissionDTO) => {
    setIsLoading(true)

    const payLoad: IRecipePermissionDTO = {
      recipeId: data.recipeId,
      execute: data.execute,
      edit: data.edit,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/recipes-permissions`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/recipes-permissions')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/recipes-permissions', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Permissões a Receitas'
        subTitle='Dados coletados sobre os logs de execução das receitas de automação.'
      />

      <Form.Body>
        <Form.Select
          label='Receita'
          error={!!errors.recipeId}
          errorMessage={errors?.recipeId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='recipeId'
          rules={{
            onChange: (e) => {
              setValue('recipeId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().recipeId}`}
          options={recipesA}
        />

        <Form.CheckBox
          label='Execução'
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='execute'
          control={control}
        />

        <Form.CheckBox
          label='Edição'
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='edit'
          control={control}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default RecipePermissionForm
